"use client";
import { useEffect } from "react";
import {Provider} from "react-redux";
import store from "../../store";
import {Toaster} from "react-hot-toast";
import { AppProgressBar as ProgressBar } from "next-nprogress-bar";
import StickyWhatsapp from "@/components/StickyWhatsapp/StickyWhatsapp";
import Script from "next/script";
import mixpanel from "mixpanel-browser";
import { messaging } from "../../firebase";
import {SkeletonTheme} from "react-loading-skeleton";
import LanguageWrapper from "@/components/LanguageWrapper";
import Bowser from "bowser";
import axios from "@/utils/axios";
import { getToken } from "firebase/messaging";
import { isFirebaseSupported } from "@/utils/isFirebaseSupported";

export default function ClientProvider({ children, footerNavbarData, treatmentsData }) {

  const isUnsupportedBrowser = () => {
    try {
      if (typeof window === "undefined" || !window.navigator) {
        return false;
      }
      const userAgent = window.navigator.userAgent || "";
      if (!userAgent) return false;
      const browser = Bowser.getParser(userAgent);
      const browserInfo = browser.getBrowser();
      const osInfo = browser.getOS();
      const browserName = browserInfo?.name?.toLowerCase?.() || "";
      const platform = osInfo?.name?.toLowerCase?.() || "";
      const unsupportedBrowsers = ["opera mini", "internet explorer"];
      const isUnsupported =
        unsupportedBrowsers.includes(browserName) ||
        platform === "macos" ||
        platform === "ios" ||
        platform === "mac";
      return isUnsupported;
    } catch (error) {
      console.error("Error checking browser support:", error);
      return false;
    }
  };

  async function sendNotification(fcmToken) {
    try {
        const response = await axios.post("notifications", {
            data: { fcm_token: fcmToken },
        });
        return response.data;
    } catch (error) {
        console.error("Error sending notification:", error);
    }
  }

  async function requestPermission() {
    // Only run in browser environment
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      !("serviceWorker" in navigator) ||
      !("indexedDB" in window)
    ) {
      console.log("Notification API not available in this environment");
      return;
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();

      // Only proceed if permission is granted
      if (permission !== "granted") {
        console.log("Notification permission not granted");
        return;
      }

      try {
        // Get FCM token
        const token = await getToken(messaging, {
          vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
        });

        // Send token to server if needed
        if (token) {
          await sendNotification(token);
        }
      } catch (tokenError) {
        console.error("Error in Firebase token generation:", tokenError);
      }
    } catch (permissionError) {
      console.error(
        "Error in notification permission request:",
        permissionError
      );
    }
  }

  useEffect(() => {
    if ("serviceWorker" in navigator && !isUnsupportedBrowser()) {
      window.addEventListener("load", async () => {
        try {
          await navigator.serviceWorker.register("/firebase-messaging-sw.js");
        } catch (error) {
          console.error("Service worker registration failed:", error);
        }
      });
    }
  }, []);

  useEffect(() => {
    isFirebaseSupported() && !isUnsupportedBrowser() && requestPermission();
  }, []);

  useEffect(() => {
    const updateConsentLink = () => {
      const link = document.getElementById("zpconsent-manage");
      if (link) {
        link.href = "/";
        link.onclick = (e) => {
          e.preventDefault();
        };
      }
    };

    const klaroScript = document.querySelector(
      'script[src="https://cdn.kiprotect.com/klaro/v0.7/klaro.js"]'
    );
    if (klaroScript) {
      klaroScript.onload = () => setTimeout(updateConsentLink, 100);
    } else {
      setTimeout(updateConsentLink, 100);
    }
  }, []);

  useEffect(() => {
    mixpanel.init("5035a77d3325828539b133277fa450ab", {
      debug: false,
      track_pageview: true,
      persistence: "localStorage",
    });
  }, []);

  return (
    <Provider store={store}>
      <SkeletonTheme>
        <LanguageWrapper
          footerNavbarData={footerNavbarData}
          treatmentsData={treatmentsData}
        >
          {children}
        </LanguageWrapper>
        <Toaster position="top-right" />
        <ProgressBar options={{ showSpinner: false }} shallowRouting />
        <StickyWhatsapp />
      </SkeletonTheme>
    </Provider>
  );
}